import { AccentBlock, DisplayItem } from '@digitools/honest-ui';
import React from 'react';
import styled from 'styled-components';
import { ReviewAccommodationDetailsText } from '../../constants/ReviewAccommodationDetailsText';
import useTranslate from 'src/hooks/useTranslate';
import useAsContext from '../../hooks/useAsConfigContext';
import { displayField, getSelectedDropDownItemsAsString } from '../../utils/AsDataUtils';
import { AS_PERIOD_TYPE_OPTIONS, AS_YES_NO_OPTIONS } from '../../constants/Constants';
import { ReviewText } from '../../constants/ReviewText';
import { CustomEmployeeText } from 'src/utils/CustomExperienceText';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
// Fields stack with a divider between them; AccentBlock pads the block itself
const RequestField = styled(DisplayItem) `
  padding-block: var(--spacing-md);
  border-block-start: 1px solid var(--color-border-default);

  &:first-child {
    padding-block-start: 0;
    border-block-start: none;
  }

  &:last-child {
    padding-block-end: 0;
  }
`;
const AddRequest = ({ count, accommodationData: accommodationRequestData, }) => {
    const { t } = useTranslate();
    const { asConfig } = useAsContext();
    const { experience } = useExperience();
    const isEmployer = experience === Experience.EMPLOYER;
    const requestedPeriodType = accommodationRequestData?.requestedPeriodType &&
        getSelectedDropDownItemsAsString(AS_PERIOD_TYPE_OPTIONS, accommodationRequestData?.requestedPeriodType, t);
    const existingAccomm = accommodationRequestData?.existingAccommodation &&
        getSelectedDropDownItemsAsString(AS_YES_NO_OPTIONS, accommodationRequestData?.existingAccommodation, t);
    const rtwSaw = accommodationRequestData?.accommodationWorkOrHome &&
        getSelectedDropDownItemsAsString(asConfig?.config?.rtwSawDropDown, accommodationRequestData?.accommodationWorkOrHome, t);
    const requestedAccommType = accommodationRequestData?.requestedAccommodationType &&
        getSelectedDropDownItemsAsString(asConfig?.config?.providedAccommodationTypeDropDown, accommodationRequestData?.requestedAccommodationType, t);
    const restrictonOrLimitation = accommodationRequestData?.restrictionsAndLimits &&
        getSelectedDropDownItemsAsString(asConfig?.config?.rlListDropDown, accommodationRequestData?.restrictionsAndLimits, t);
    const status = accommodationRequestData?.status &&
        getSelectedDropDownItemsAsString(asConfig?.config?.requestStatusDropDown, accommodationRequestData?.status, t);
    const assistanceRequired = accommodationRequestData?.assistanceRequired &&
        getSelectedDropDownItemsAsString(asConfig?.config?.assistanceDropDown, accommodationRequestData?.assistanceRequired, t);
    const priorCarrier = accommodationRequestData?.priorCarrier &&
        getSelectedDropDownItemsAsString(AS_YES_NO_OPTIONS, accommodationRequestData?.priorCarrier, t);
    const providedAccomodationTypeCode = accommodationRequestData?.providedAccomodationTypeCode &&
        getSelectedDropDownItemsAsString(asConfig?.config?.providedAccommodationTypeDropDown, accommodationRequestData?.providedAccomodationTypeCode, t);
    const statusReasonCode = accommodationRequestData?.statusReasonCode &&
        getSelectedDropDownItemsAsString(asConfig?.config?.statusReasonDropDown, accommodationRequestData?.statusReasonCode, t);
    const isAsRep = isEmployer && !!asConfig?.config?.asRep;
    const isNew = accommodationRequestData?.status === 'IP';
    const isClosed = accommodationRequestData?.status === 'CL';
    const isTemporary = accommodationRequestData?.requestedPeriodType === 'T';
    // Same order and show conditions as before the rebrand; only the markup changed
    const fields = [
        {
            // The accommodation type is the label and its description is the value, as in the mock
            show: requestedAccommType || accommodationRequestData?.requestedAccommodationDetail,
            label: requestedAccommType && <span data-private={true}>{requestedAccommType}</span>,
            value: accommodationRequestData?.requestedAccommodationDetail,
            isPrivate: true,
        },
        {
            show: isAsRep && accommodationRequestData?.requestReceivedDate,
            label: CustomEmployeeText(t(ReviewText.addRequest.requestReceivedDate)),
            value: accommodationRequestData?.requestReceivedDate,
        },
        { show: isAsRep && status, label: t(ReviewText.addRequest.status), value: status },
        {
            show: isAsRep && isClosed && accommodationRequestData?.closedDate,
            label: t(ReviewText.addRequest.closedDate),
            value: accommodationRequestData?.closedDate,
        },
        {
            show: isAsRep && isClosed && statusReasonCode,
            label: t(ReviewText.addRequest.statusReasonCode),
            value: statusReasonCode,
        },
        { show: isAsRep && assistanceRequired, label: t(ReviewText.addRequest.assistRequired), value: assistanceRequired },
        {
            show: isAsRep && !isNew && providedAccomodationTypeCode,
            label: t(ReviewText.addRequest.providedAccomodationTypeCode),
            value: providedAccomodationTypeCode,
            isPrivate: true,
        },
        {
            show: isAsRep && !isNew && accommodationRequestData?.providedDetailsText,
            label: t(ReviewText.addRequest.providedDetailsText),
            value: accommodationRequestData?.providedDetailsText,
            isPrivate: true,
        },
        {
            show: !(isAsRep && !isNew) && requestedPeriodType,
            label: t(ReviewAccommodationDetailsText.expectedDurationEE),
            value: requestedPeriodType,
            isPrivate: true,
        },
        {
            show: isAsRep && !isNew && requestedPeriodType,
            label: t(ReviewText.addRequest.actualPeriodType),
            value: requestedPeriodType,
            isPrivate: true,
        },
        {
            show: !isClosed && accommodationRequestData?.requestedStartDate,
            label: t(ReviewAccommodationDetailsText.desiredStartDateEE),
            value: accommodationRequestData?.requestedStartDate,
        },
        {
            show: isAsRep && !isNew && accommodationRequestData?.actualBeginDate,
            label: t(ReviewText.addRequest.actualBeginDate),
            value: accommodationRequestData?.actualBeginDate,
        },
        {
            show: !isClosed && isTemporary && accommodationRequestData?.anticipatedEndDate,
            label: t(ReviewAccommodationDetailsText.anticipatedEndDate),
            value: accommodationRequestData?.anticipatedEndDate,
        },
        {
            show: isAsRep && !isNew && isTemporary && accommodationRequestData?.actualEndDate,
            label: t(ReviewText.addRequest.actualEndDate),
            value: accommodationRequestData?.actualEndDate,
        },
        {
            show: isAsRep && restrictonOrLimitation,
            label: t(ReviewText.addRequest.restrictonOrLimitation),
            value: restrictonOrLimitation,
            isPrivate: true,
        },
        {
            show: isAsRep &&
                displayField(accommodationRequestData?.restrictionsAndLimits) &&
                accommodationRequestData?.suppRestrictionsAndLimitations,
            label: t(ReviewText.addRequest.suppRestrictionsAndLimitations),
            value: accommodationRequestData?.suppRestrictionsAndLimitations,
            isPrivate: true,
        },
        {
            show: isAsRep && accommodationRequestData?.interactiveBeginDate,
            label: CustomEmployeeText(t(ReviewText.addRequest.interactiveBeginDate)),
            value: accommodationRequestData?.interactiveBeginDate,
        },
        {
            show: isAsRep && accommodationRequestData?.initialMeetingDate,
            label: CustomEmployeeText(t(ReviewText.addRequest.initialMeetingDate)),
            value: accommodationRequestData?.initialMeetingDate,
        },
        {
            show: isAsRep && !isNew && accommodationRequestData?.determinationInfoReceivedDate,
            label: CustomEmployeeText(t(ReviewText.addRequest.determinationInfoReceivedDate)),
            value: accommodationRequestData?.determinationInfoReceivedDate,
        },
        {
            show: isAsRep && !isNew && accommodationRequestData?.determinationDate,
            label: t(ReviewText.addRequest.determinationDate),
            value: accommodationRequestData?.determinationDate,
        },
        { show: existingAccomm, label: t(ReviewAccommodationDetailsText.alreadyReportedEE), value: existingAccomm },
        { show: isAsRep && priorCarrier, label: t(ReviewText.addRequest.priorCarrier), value: priorCarrier },
        {
            show: isAsRep && !isClosed && accommodationRequestData?.requestedAccommodationCost,
            label: t(ReviewText.addRequest.requestedAccommCost),
            value: accommodationRequestData?.requestedAccommodationCost,
        },
        {
            show: isAsRep && !isNew && accommodationRequestData?.providedCostAmount,
            label: t(ReviewText.addRequest.providedCostAmount),
            value: accommodationRequestData?.providedCostAmount,
        },
        { show: rtwSaw, label: t(ReviewAccommodationDetailsText.willReturnToWorkEE), value: rtwSaw },
        {
            show: isAsRep && isClosed && isTemporary && accommodationRequestData?.rtwWithoutAccomodationDate,
            label: CustomEmployeeText(t(ReviewText.addRequest.rtwWithoutAccomodationDate)),
            value: accommodationRequestData?.rtwWithoutAccomodationDate,
        },
    ];
    return (
    // AccentBlock hides the number from screen readers; the parent <ol> announces the position
    <AccentBlock as='li' number={count} className='hui-mb-md' data-testid='review-add-request'>
      {fields
            .filter(field => field.show)
            .map((field, index) => (<RequestField key={index} label={field.label} value={field.isPrivate ? <span data-private={true}>{field.value}</span> : field.value}/>))}
    </AccentBlock>);
};
export default React.memo(AddRequest);
